import React from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  PieChart,
  BarChart3,
  Users,
  Package,
  Eye,
  MoreHorizontal,
  Calendar,
  Target,
  Activity,
  Building
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart as RechartsPieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import StatCard from '../../components/StatCard';

const FinancialDashboard: React.FC = () => {
  const { user } = useAuth();


  const monthlyRevenue = [
    { month: 'Jul', revenue: 285000, cost: 170000, profit: 115000 },
    { month: 'Aug', revenue: 310000, cost: 185000, profit: 125000 },
    { month: 'Sep', revenue: 295000, cost: 175000, profit: 120000 },
    { month: 'Oct', revenue: 340000, cost: 200000, profit: 140000 },
    { month: 'Nov', revenue: 325000, cost: 195000, profit: 130000 },
    { month: 'Dec', revenue: 365000, cost: 215000, profit: 150000 }
  ];

  const revenueByCategory = [
    { name: 'Cardiovascular', value: 125000, color: '#8B5CF6' },
    { name: 'Endocrine', value: 95000, color: '#10B981' },
    { name: 'Mental Health', value: 75000, color: '#F59E0B' },
    { name: 'Gastrointestinal', value: 65000, color: '#EF4444' },
    { name: 'Respiratory', value: 55000, color: '#3B82F6' },
    { name: 'Other', value: 45000, color: '#6B7280' }
  ];

  const customerTypeRevenue = [
    { type: 'Aged Care', revenue: 180000, margin: 42, orders: 245 },
    { type: 'NDIS', revenue: 120000, margin: 38, orders: 156 },
    { type: 'Retail', revenue: 95000, margin: 45, orders: 420 },
    { type: 'Hospital', revenue: 85000, margin: 35, orders: 89 }
  ];

  const topPerformingMedications = [
    { name: 'Atorvastatin 20mg', revenue: 15500, margin: 48, units: 850 },
    { name: 'Metformin 500mg', revenue: 12800, margin: 42, units: 720 },
    { name: 'Amlodipine 5mg', revenue: 11200, margin: 45, units: 650 },
    { name: 'Omeprazole 20mg', revenue: 9800, margin: 40, units: 580 },
    { name: 'Sertraline 50mg', revenue: 8900, margin: 38, units: 520 }
  ];

  const paymentStatus = [
    { status: 'Paid', amount: 285000, count: 156, color: '#10B981' },
    { status: 'Pending', amount: 45000, count: 23, color: '#F59E0B' },
    { status: 'Overdue', amount: 12000, count: 8, color: '#EF4444' }
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Financial Dashboard 💰
          </h1>
          <p className="text-gray-600 mt-2">Revenue analytics, profitability metrics, and financial KPIs for {user?.branch}.</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Financial Period</p>
          <p className="text-sm font-medium text-gray-900">{format(new Date(), 'MMM yyyy')}</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Monthly Revenue"
          value="$365K"
          icon={<DollarSign className="h-6 w-6" />}
          trend="vs last month"
          trendValue={12.3}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Gross Profit"
          value="$150K"
          icon={<TrendingUp className="h-6 w-6" />}
          trend="vs last month"
          trendValue={15.4}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Profit Margin"
          value="41.1%"
          icon={<Target className="h-6 w-6" />}
          trend="vs last month"
          trendValue={2.1}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Outstanding AR"
          value="$57K"
          icon={<CreditCard className="h-6 w-6" />}
          trend="vs last month"
          trendValue={-8.2}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Payment Status Overview */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Payment Status Overview</h3>
            <p className="text-gray-600 text-sm">Current accounts receivable status</p>
          </div>
          <Link to="/invoicing/payments" className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1">
            <span>View Payments</span>
            <Eye className="h-4 w-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {paymentStatus.map((status, index) => (
            <div key={index} className="bg-gray-50/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: status.color }}></div>
                  <h4 className="font-semibold text-gray-900">{status.status}</h4>
                </div>
                <span className="text-sm text-gray-500">{status.count} invoices</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">${status.amount.toLocaleString()}</p>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full" 
                  style={{ 
                    width: `${(status.amount / 342000) * 100}%`, 
                    backgroundColor: status.color 
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Revenue & Profit Trends */}
        <div className="lg:col-span-2">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Revenue & Profit Trends</h3>
                <p className="text-gray-600 text-sm">Monthly financial performance overview</p>
              </div>
              <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <MoreHorizontal className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={monthlyRevenue}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: 'none', 
                    borderRadius: '12px', 
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                  }} 
                />
                <Area type="monotone" dataKey="revenue" stroke="#8B5CF6" fillOpacity={1} fill="url(#colorRevenue)" />
                <Area type="monotone" dataKey="profit" stroke="#10B981" fillOpacity={1} fill="url(#colorProfit)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue by Category */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Revenue by Category</h3>
              <p className="text-gray-600 text-sm">Medication category breakdown</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <RechartsPieChart>
              <Pie
                data={revenueByCategory}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {revenueByCategory.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: 'none', 
                  borderRadius: '12px', 
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                }} 
              />
            </RechartsPieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {revenueByCategory.slice(0, 4).map((item, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-gray-700">{item.name}</span>
                </div>
                <span className="font-semibold text-gray-900">${item.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Customer Type Performance & Top Medications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Customer Type Revenue */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Revenue by Customer Type</h3>
              <p className="text-gray-600 text-sm">Performance across customer segments</p>
            </div>
          </div>
          <div className="space-y-4">
            {customerTypeRevenue.map((customer, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl hover:bg-gray-100/50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Building className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{customer.type}</p>
                    <p className="text-sm text-gray-500">{customer.orders} orders • {customer.margin}% margin</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">${customer.revenue.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Revenue</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performing Medications */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Top Performing Medications</h3>
              <p className="text-gray-600 text-sm">Highest revenue generating products</p>
            </div>
            <Link to="/reports/financial" className="text-purple-600 hover:text-purple-700 text-sm font-medium">View All</Link>
          </div>
          <div className="space-y-4">
            {topPerformingMedications.map((med, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl hover:bg-gray-100/50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{med.name}</p>
                    <p className="text-sm text-gray-500">{med.units} units • {med.margin}% margin</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">${med.revenue.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Revenue</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;
